/**
 * Supabase client configuration.
 */

import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Debug logging in development
if (import.meta.env.DEV) {
  console.log('Supabase Config:', {
    url: supabaseUrl ? `${supabaseUrl.substring(0, 30)}...` : 'MISSING',
    key: supabaseAnonKey ? `${supabaseAnonKey.substring(0, 20)}...` : 'MISSING',
  });
}

if (!supabaseUrl || !supabaseAnonKey) {
  const missing = [];
  if (!supabaseUrl) missing.push('VITE_SUPABASE_URL');
  if (!supabaseAnonKey) missing.push('VITE_SUPABASE_ANON_KEY');
  
  // In production, log to console instead of throwing to prevent white screen
  const errorMsg = `Missing Supabase environment variables: ${missing.join(', ')}. ` +
    `Please set these in Cloudflare Pages → Settings → Environment Variables. ` +
    `See docs/SUPABASE_API_KEY_TROUBLESHOOTING.md for help.`;
  
  console.error(errorMsg);
  
  // Only throw in development to help catch issues early
  if (import.meta.env.DEV) {
    throw new Error(errorMsg);
  }
}

// Validate API key format (should start with 'eyJ' for JWT)
if (supabaseAnonKey && !supabaseAnonKey.startsWith('eyJ')) {
  console.warn(
    'Warning: VITE_SUPABASE_ANON_KEY does not appear to be a valid JWT token. ' +
    'Make sure you are using the "anon public" key from Supabase, not the "service_role" key. ' +
    'See docs/SUPABASE_API_KEY_TROUBLESHOOTING.md for help.'
  );
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

/**
 * Extract org_id and roles from JWT token.
 */
export function extractJwtClaims(token: string): { org_id?: string; roles?: string[] } {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      org_id: payload.org_id,
      roles: payload.roles || [],
    };
  } catch {
    return {};
  }
}

/**
 * Get current session and extract org context.
 */
export async function getSessionWithOrg() {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error('Error getting session:', error);
      return null;
    }
    
    if (!session) {
      return null;
    }

    const claims = extractJwtClaims(session.access_token);
    return {
      session,
      orgId: claims.org_id,
      roles: claims.roles || [],
    };
  } catch (error) {
    console.error('Exception getting session:', error);
    return null;
  }
}

